import React, { useState } from 'react'
import { DataTable, ColumnDefinition, CellDefinition } from 'common/components/DataTable'
import Utils from 'common/Utils'
import * as Icons from 'react-bootstrap-icons'
import { Button } from 'react-bootstrap'
import OperationWorkflowModal from 'common/components/OperationWorkflowModal'
import EditExpectedMovementWorkflow from 'application/budget/plan/EditExpectedMovementWorkflow'
import API from 'common/logic/API'
import _ from 'lodash'
import { BudgetPlan, AmountByTag, ExpectedMovement, Tag } from 'types'

interface PlanData {
    total: Record<string, number>
    entries: Record<string, AmountByTag[]>
}

interface Props {
    currentPlan: BudgetPlan
    data: PlanData
    expectedMovementsByCurrency?: Record<string, ExpectedMovement[]> | null
    refreshCallback: () => void
}

function ExpectedMovementsByTagTable({ currentPlan, data, expectedMovementsByCurrency, refreshCallback }: Props) {
    const [modal, setModal] = useState<{ type: string; id?: number; name?: string } | null>(null)

    const hideDialogs = () => setModal(null)
    const showDeletionDialog = (id: number, name: string) => setModal({ type: "delete", id, name })
    const showCreateDialog = () => setModal({ type: "create" })
    const showEditDialog = (id: number) => setModal({ type: "edit", id })

    const refreshData = () => refreshCallback()

    const deletionModal = (params: { id: number; name: string }) => {
        const confirmationStep = () => `Delete expected movement "${params.name}"?`
        const executeStep = () => API.delete(`/expected-movements/${params.id}`)
        const completion = (result: unknown) => {
            hideDialogs()
            if (result) refreshData()
        }
        return <OperationWorkflowModal
            confirmationStep={confirmationStep}
            executionStep={executeStep}
            completionCallback={completion}
        />
    }

    const creationOrEditModal = (params: { id?: number }) => {
        const completion = (result: unknown) => {
            hideDialogs()
            if (result) refreshData()
        }
        return <EditExpectedMovementWorkflow
            currentPlanId={currentPlan.id}
            onSuccess={completion}
            entryId={params.id}
        />
    }

    let modalElement: React.ReactNode = null
    if (modal) {
        switch (modal.type) {
            case "delete":
                modalElement = deletionModal(modal as { id: number; name: string })
                break
            case "create":
            case "edit":
                modalElement = creationOrEditModal(modal)
                break
            default:
        }
    }

    const currencies = Object.keys(data.total)
    return <>
        {modalElement}
        <Button variant="link" onClick={showCreateDialog}>Add new expected movement</Button>
        {currencies.map(curr => {
            const movementsByTag = expectedMovementsByCurrency
                ? groupMovementsByTag(expectedMovementsByCurrency[curr] ?? [])
                : null
            return <React.Fragment key={curr}>
                {currencies.length > 1 ? <h2>{curr}</h2> : ''}
                <SingleCurrencyExpenseByTagTable
                    currency={curr}
                    entries={data.entries[curr] ?? []}
                    total={data.total[curr]}
                    movementsByTag={movementsByTag}
                    onEdit={showEditDialog}
                    onDelete={showDeletionDialog}
                />
            </React.Fragment>
        })}
    </>
}

// Groups movements by tag. A movement with multiple tags appears under each tag.
// Movements with no tags appear under the null key (untagged).
function groupMovementsByTag(movements: ExpectedMovement[]): Record<string, ExpectedMovement[]> {
    const result: Record<string, ExpectedMovement[]> = {}
    movements.forEach(m => {
        if (!m.tags || m.tags.length === 0) {
            const key = 'untagged'
            result[key] = [...(result[key] ?? []), m]
        } else {
            m.tags.forEach((tag: Tag) => {
                const key = String(tag.id)
                result[key] = [...(result[key] ?? []), m]
            })
        }
    })
    return result
}

interface SingleCurrencyProps {
    currency: string
    entries: AmountByTag[]
    total: number
    movementsByTag: Record<string, ExpectedMovement[]> | null
    onEdit: (id: number) => void
    onDelete: (id: number, name: string) => void
}

function SingleCurrencyExpenseByTagTable({ currency, entries, total, movementsByTag, onEdit, onDelete }: SingleCurrencyProps) {
    const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({})

    const expandTag = (key: string) => setExpandedRows(prev => ({ ...prev, [key]: true }))
    const collapseTag = (key: string) => setExpandedRows(prev => ({ ...prev, [key]: false }))

    const tagCell = (tag: Tag | null) => {
        const key = tag ? String(tag.id) : 'untagged'
        const label = tag ? tag.name : 'Untagged'
        let button: React.ReactNode = null
        let extension: React.ReactNode = null
        if (movementsByTag) {
            const movements = movementsByTag[key]
            const isOpen = expandedRows[key]
            const icon = isOpen ? <Icons.ChevronUp /> : <Icons.ChevronDown />
            const onClick = () => {
                isOpen ? collapseTag(key) : expandTag(key)
            }
            button = <Button variant="link" onClick={onClick}>{icon}</Button>
            extension = isOpen && movements ? <div className="subtable-indent">
                <ExpectedMovementsReducedTable
                    movements={movements}
                    onEdit={onEdit}
                    onDelete={onDelete}
                />
            </div> : null
        }
        return <>
            {button}
            {label}
            {' '}
            {extension}
        </>
    }

    const sumByCurrency = [
        new CellDefinition(`TOTAL`, "td-total"),
        new CellDefinition(<>{Utils.formatMoney(total, true)} {currency}</>, "td-currency")
    ]

    const columnDefinitions = [
        new ColumnDefinition<AmountByTag>("Tag", obj => tagCell(obj.tag)),
        new ColumnDefinition<AmountByTag>("Amount", obj => <>{Utils.formatMoney(obj.amount, true)} {currency}</>, "td-currency", "td-right")
    ]

    return <>
        <DataTable
            columns={columnDefinitions}
            rows={entries}
            summaryRows={[sumByCurrency]}
        />
    </>
}

interface ReducedTableProps {
    movements: ExpectedMovement[]
    onEdit: (id: number) => void
    onDelete: (id: number, name: string) => void
}

function ExpectedMovementsReducedTable({ movements, onEdit, onDelete }: ReducedTableProps) {
    const columnDefinitions = [
        new ColumnDefinition<ExpectedMovement>("Entry", "summary"),
        new ColumnDefinition<ExpectedMovement>("Monthly amount", obj => <>{Utils.formatMoney(obj.monthlyAmount, true)} {obj.currency.name}</>, "td-currency", "td-right"),
        new ColumnDefinition<ExpectedMovement>("Actions", obj => <>
            <Button variant="link" title="Edit"
                onClick={() => onEdit(obj.id)}><Icons.Pencil /></Button> |{' '}
            <Button variant="link" title="Delete"
                onClick={() => onDelete(obj.id, obj.summary as string)}><Icons.Trash /></Button>
        </>)
    ]

    return <DataTable
        columns={columnDefinitions}
        rows={movements}
    />
}

export default ExpectedMovementsByTagTable
